import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, RadioGroup, RadioGroupItem } from '@nimbitlabs/ui';
import { toast } from 'sonner';
import { Bell, User, AlertCircle, CheckCircle, AlertTriangle, Info, Link as LinkIcon, BarChart } from 'lucide-react';
import { useUsersStore } from '../users/usersStore';
import { useNotificationsStore } from './notificationsStore';

export interface SendNotificationUser {
  id: string;
  name: string;
  email: string;
}

export interface NotificationData {
  userId: string;
  type: 'alert' | 'report' | 'system';
  title: string;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  actionUrl?: string;
  actionText?: string;
}

interface SendNotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: {
    title?: string;
    message?: string;
    type?: 'alert' | 'report' | 'system';
    severity?: 'info' | 'success' | 'warning' | 'error';
    recipientUserId?: string;
  };
}

export function SendNotificationModal({ 
  open, 
  onOpenChange, 
  initialData
}: SendNotificationModalProps) {
  const { users = [], fetchUsers } = useUsersStore();
  const { sendNotification } = useNotificationsStore();

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, fetchUsers]);
  
  const [selectedUserId, setSelectedUserId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'alert' | 'report' | 'system'>('system');
  const [severity, setSeverity] = useState<'info' | 'success' | 'warning' | 'error'>('info');
  const [link, setLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      if (initialData.title) setTitle(initialData.title);
      if (initialData.message) setMessage(initialData.message);
      if (initialData.type) setType(initialData.type);
      if (initialData.severity) setSeverity(initialData.severity);
      if (initialData.recipientUserId) {
        setSelectedUserId(initialData.recipientUserId);
      }
    }
  }, [open, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendNotification({
        userId: selectedUserId,
        type,
        title: title.trim(),
        message: message.trim(),
        severity,
        actionUrl: link.trim() || undefined,
        actionText: link.trim() ? 'View' : undefined,
      });

      toast.success('Notification sent successfully!');
      
      // Reset form
      setSelectedUserId('');
      setTitle('');
      setMessage('');
      setType('system');
      setSeverity('info');
      setLink('');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[88vh] overflow-hidden flex flex-col z-50">
        <DialogHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2.5">
                <DialogTitle className="text-lg">Send Notification</DialogTitle>
              </div>
              <DialogDescription className="mt-0.5 text-xs">
                Deliver real-time notifications to users or teams
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-6 px-2">
          {/* Recipient Section */}
          <div className="space-y-3 pl-8 border-l-2 border-primary/30 pb-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-foreground/70 uppercase tracking-wide">
              <User className="w-3.5 h-3.5 text-primary" />
              <span>Recipient</span>
            </div>
            
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select user..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {users.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No users available
                  </div>
                ) : (
                  users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5" />
                        <span>{user.name}</span>
                        <span className="text-xs text-muted-foreground">({user.email})</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Notification Details */}
          <div className="space-y-4 pl-8 border-l-2 border-primary/30 pt-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-foreground/70 uppercase tracking-wide -mt-2">
              <Bell className="w-3.5 h-3.5 text-primary" />
              <span>Notification Details</span>
            </div>

            <div className="space-y-2.5">
              <Label className="text-xs">Type</Label>
              <RadioGroup 
                value={type} 
                onValueChange={(value: any) => setType(value)}
                className="flex gap-2"
              >
                <Label
                  htmlFor="alert-type"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    type === 'alert' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-accent/50'
                  }`}
                >
                  <RadioGroupItem value="alert" id="alert-type" className="sr-only" />
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Alert</span>
                </Label>
                
                <Label
                  htmlFor="report-type"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    type === 'report' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-accent/50'
                  }`}
                >
                  <RadioGroupItem value="report" id="report-type" className="sr-only" />
                  <BarChart className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Report</span>
                </Label>
                
                <Label
                  htmlFor="system-type"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    type === 'system' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-accent/50'
                  }`}
                >
                  <RadioGroupItem value="system" id="system-type" className="sr-only" />
                  <Bell className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">System</span>
                </Label>
              </RadioGroup>
            </div>

            <div className="space-y-2.5">
              <Label className="text-xs">Severity</Label>
              <RadioGroup 
                value={severity} 
                onValueChange={(value: any) => setSeverity(value)}
                className="flex gap-2"
              >
                <Label
                  htmlFor="info-severity"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    severity === 'info' 
                      ? 'border-blue-500 bg-blue-500/5' 
                      : 'border-border hover:border-blue-500/50 hover:bg-blue-500/5'
                  }`}
                >
                  <RadioGroupItem value="info" id="info-severity" className="sr-only" />
                  <Info className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium">Info</span>
                </Label>
                
                <Label
                  htmlFor="success-severity"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    severity === 'success' 
                      ? 'border-green-500 bg-green-500/5' 
                      : 'border-border hover:border-green-500/50 hover:bg-green-500/5'
                  }`}
                >
                  <RadioGroupItem value="success" id="success-severity" className="sr-only" />
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-xs font-medium">Success</span>
                </Label>
                
                <Label
                  htmlFor="warning-severity"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    severity === 'warning' 
                      ? 'border-orange-500 bg-orange-500/5' 
                      : 'border-border hover:border-orange-500/50 hover:bg-orange-500/5'
                  }`}
                >
                  <RadioGroupItem value="warning" id="warning-severity" className="sr-only" />
                  <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                  <span className="text-xs font-medium">Warning</span>
                </Label>
                
                <Label
                  htmlFor="error-severity"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    severity === 'error' 
                      ? 'border-red-500 bg-red-500/5' 
                      : 'border-border hover:border-red-500/50 hover:bg-red-500/5'
                  }`}
                >
                  <RadioGroupItem value="error" id="error-severity" className="sr-only" />
                  <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-medium">Error</span>
                </Label>
              </RadioGroup>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="title" className="text-xs">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief, clear notification title"
                className="h-9"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="message" className="text-xs">Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Detailed notification message..."
                rows={3}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="link" className="flex items-center gap-1.5 text-xs">
                <LinkIcon className="w-3 h-3" />
                Action Link (optional)
              </Label>
              <Input
                id="link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/services or https://example.com"
                className="h-9"
              />
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2.5 pt-4 border-t border-border/30">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-w-[90px] h-9"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            onClick={handleSubmit}
            className="min-w-[120px] h-9 bg-gradient-to-r from-primary to-primary/90"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Bell className="w-3.5 h-3.5" />
                Send
              </span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
